/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The org's billing/entitlement vocabulary (AGL-443 naming cleanup).
 *
 * HISTORY: these types were spelled `Tenant*` until AGL-444 — they
 * predate the organizations migration (AGL-232..238); the retired
 * `tenants/{uid}` collection's billing shape was mirrored ONTO the org
 * doc and the names came along. The alias is gone: everything here is
 * `Org*` and describes fields of `orgs/{orgId}`. The last persisted
 * tenant spellings (the `users.{uid}.tenants` map, Stripe
 * `metadata[tenantId]`, `host.tenantId`) were retired pre-launch in
 * AGL-445 — billing keys off `metadata[orgId]` now.
 *
 * Convention (see the docs-site glossary): "organization/org" is the
 * entity; "workspace" is the user-facing word for it; "tenant" is
 * reserved for the published-site runtime (`apps/tenant`,
 * `@aglyn/tenant-*` libs).
 */

import type { ITimestamp } from '@aglyn/shared-util-timestamp'
import type {
  AglynDocument,
  HostUid,
  OrgUid,
  UserUid,
} from './platform.types'
/*
 * The plugin half of the two composed shapes below (AGL-3124).
 *
 * Each is an EMPTY interface a plugin augments with `declare module`, so the
 * keys a plugin owns are part of `OrgEntitlements` and `OrgFeatureFlags`
 * without the core listing them. The AI credits, the CRM's one-to-one email
 * pace, the form-submission and workflow-run bands, the marketplace's take
 * rate and selling gate and commerce's three gated features are declared that
 * way, each in its plugin's `plan-entitlements` module (AGL-3080).
 *
 * `import type` and nothing else: TypeScript erases it, so the plugin-manager
 * module is not on this module's runtime graph and a published page that
 * reaches the billing types (through the foundation barrel it must not reach
 * anyway) carries nothing new.
 */
import type {
  PluginEntitlementFeatures,
  PluginEntitlementQuotas,
} from '../../plugin-manager/plugin-entitlement-keys'

export type { OrgUid } from './platform.types'


/** Hosted in master catalog */
/**
 * SaaS subscription tiers (Tenant Billing & SaaS Plans, AGL-38..41).
 * Pricing v3 (2026-07) inserted `scale` between business and advanced to
 * fill the $139→$399 gap, and added `agency` above advanced for
 * high-volume multi-site orgs — see the Pricing Decision Log.
 *
 * `enterprise` (AGL-1118) is a REAL plan, not a display label: it tops the
 * ladder with unlimited capacity plus white-label and SSO, and it is the ONE
 * tier with no list price — it is staff-provisioned per deal (AGL-1110), never
 * self-serve. Surfaces that offer plans for sale iterate `SELF_SERVE_PLANS`,
 * which excludes it; surfaces that merely NAME the org's plan read
 * `PLAN_LABELS` and get "Enterprise" for free.
 */
export type OrgPlan =
  | 'free'
  | 'starter'
  | 'pro'
  | 'business'
  | 'scale'
  | 'advanced'
  | 'agency'
  | 'enterprise'

/**
 * The CORE boolean feature gates per plan; quotas live beside them as
 * numbers. Read {@link OrgFeatureFlags} instead — this half exists so the
 * plan tables can stay exhaustive over the platform's own gates while a
 * plugin declares its own (AGL-3124).
 */
export interface CoreOrgFeatureFlags {
  /** A/B experiments (AGL-252); Business tier. */
  abTesting?: boolean
  versioning?: boolean
  /**
   * Reusable components WITHOUT a count: Starter and above. Since AGL-3615 a
   * component's create is counted against `componentsPerHost` alone — Free
   * saves one — so this flag no longer decides whether a site may make one.
   * It states that the allowance is unlimited, and a per-org override that
   * turns it on lifts `componentsPerHost` with it, which is how an org
   * granted the feature by contract before the count existed keeps it.
   */
  reusableComponents?: boolean
  customDomain?: boolean
  /**
   * Send mail as a domain the CUSTOMER owns — `hello@acme.com` — by publishing
   * our SPF, DKIM and return-path records in their own zone.
   *
   * Distinct from `customDomain`, which is the site's public web address and
   * authorizes nothing about mail; and distinct from `whiteLabel`, which
   * replaces the Aglyn brand — product name, logo, colors, support URL,
   * console chrome, favicon — across every branded surface. Sending as your
   * own name is one narrow consequence of white-labeling rather than the
   * whole of it, so the two cannot share a flag without the wider capability
   * following the narrower one wherever it goes.
   *
   * It is also the CHEAP half of the sending model, which is why it sits low
   * on the ladder. A customer-owned domain costs the platform one provider
   * domain object and NOTHING in our own DNS zone, because the customer
   * publishes the records; the platform subdomain a site is issued costs a
   * provider slot, three records in our zone and a permanent place in the
   * re-verification sweep. Holding the free-to-us option behind the highest
   * paywall makes the expensive one the default at every tier that can send.
   *
   * Read through `checkEntitlement` at the two routes that write a sending
   * identity: the org's domain list, and the per-site selection.
   */
  customSendingDomain?: boolean
  /**
   * Hold a sending subdomain of the PLATFORM's own —
   * `hello@{label}.mail.aglyn.app` — provisioned for one site inside Aglyn's
   * mail apex.
   *
   * The EXPENSIVE half of the sending model, and the reason it is a separate
   * flag from `customSendingDomain`. A domain the customer owns costs one
   * provider domain object and nothing in our zone, because they publish the
   * records. One of these costs a provider slot, THREE records in our own
   * zone, and a permanent place in the re-verification sweep — per site,
   * forever. The two capabilities are decided against different resources, so
   * a shared flag would move a bounded one whenever the unbounded one is
   * re-cut.
   *
   * The flag is the SECOND of two conditions and never the only one. Nothing
   * provisions on entitlement alone: a merchant asks from the sending-identity
   * route, this says whether the org carries what they asked for, and the
   * provider ceiling still refuses beyond the account's allowance. A site
   * refused at any of the three sends on the shared pool rather than not at
   * all.
   *
   * Read through `checkEntitlement` at
   * `libs/aglyn/src/lib/app-utils/dedicated-sending-domain.ts`, which is the
   * only reader — the claim path in `host-sending-domain.ts` goes through it.
   */
  dedicatedSendingDomain?: boolean
  removeBranding?: boolean
  /** Schedule a version to publish at a date/time (tier above versioning). */
  scheduledPublishing?: boolean
  /** AI copy assist in the besigner (AGL-89). */
  aiAssist?: boolean
  /**
   * Generative building and automation (AGL-2896): pages, components,
   * layouts, templates, SEO, email, campaigns, A/B variants, analytics
   * insights, products, CRM records, onboarding and workflows produced by
   * the assistant rather than assembled by hand.
   *
   * Distinct from `aiAssist`, which is the chat guide, copy assist and
   * generate-section rung every tier from Pro up carries. This is the
   * expensive rung: a generated surface carries the node tree, the catalog
   * and the theme in and iterates, so it draws credits by the hundreds where
   * a question draws tens. No self-serve tier includes it; the Aglyn AI
   * add-on (`OrgSeatAddons.aiAddon`) switches it on and adds the credit band
   * that funds it, and `resolveOrgEntitlements` flips this flag together
   * with `aiAssist` when the add-on is present. Enterprise carries it in the
   * agreement.
   */
  aiGenerative?: boolean
  /** No-code workflow builder (AGL-101). */
  workflows?: boolean
  /** Datasets + repeatable components (AGL-102/103). */
  dataStore?: boolean
  /** Video/file uploads in the media manager (AGL-162). */
  videoMedia?: boolean
  /** Appointment bookings (AGL-159). */
  bookings?: boolean
  /**
   * THE CRM (AGL-2611): contacts, leads, companies, the deals pipeline,
   * tasks, reports and custom fields, the CRM automation steps, and the
   * `crm:*` REST resources.
   *
   * The Contacts section included (AGL-2851). Capture still writes a site's
   * audience into the contacts collection on every plan including Free,
   * banded by `contactsPerHost`, and exporting or erasing those people stays
   * on every plan in Settings → Privacy. What this flag gates is the CRM a
   * team works them in, and it is the upgrade motive from Free to the first
   * paid tier rather than a line on top of one: for the small-business buyer
   * the CRM is the reason to pick a platform over a page builder.
   *
   * Read by the console shell through the CRM extension's and its widgets'
   * `featureFlag` (so the shell refuses every CRM section and page before
   * one mounts), by the `crm/*` routes, by the automation executor before a
   * CRM step runs, by the REST dispatcher in front of the CRM resources,
   * contacts included, and, restated, by the Firestore rules on a client's
   * CRM writes. A per-org override on `entitlements.features.crm` works the
   * way every other flag's does.
   */
  crm?: boolean
  /**
   * SEQUENCES (AGL-2974): one-to-one, multi-step email sequences a rep sends
   * from their own connected mailbox, logged on the CRM's records. The key
   * below keeps the name the feature shipped under, because it is stored on
   * the org (AGL-3199).
   *
   * False on EVERY plan, Enterprise included. Which tiers carry sequences
   * and connected mailboxes, and at what caps, is a packaging decision that
   * has not been made, so no plan may claim it. An organization reaches it
   * only through the per-org override on `entitlements.features.outreach`.
   *
   * Read by the console shell through the Sequences extension's
   * `featureFlag`, by the plugin's routes, and, restated, by the Firestore
   * rules in front of the `outreach*` collections. The rules carry no plan
   * list for it because no plan grants it; when packaging lands, the plan
   * table and those rules change together.
   */
  outreach?: boolean
  /**
   * Basic presentational interactions (AGL-577): menu/drawer open-close,
   * element show/hide, class toggles, sticky nav, navigation, site
   * alerts. Included on ALL plans — pure client-side DOM with no server
   * cost. The `actions` flag below gates the powerful automation steps
   * (server dispatch, runJs, analytics, overlays, raw HTML).
   *
   * `true` on all eight tiers, and NO code gates on it — which makes it look
   * dead, and it has now been filed as dead once (AGL-2082). It is not.
   * `tools/marketing/build-pricing-tables.mts` reads its value to emit the
   * "Interactions" row on the public /pricing compare table, ticked on every
   * plan, and the Free plan card bullets it. That row is the claim that a
   * hover-to-open menu is not a paid feature — a real competitive statement,
   * and the reason `true` everywhere is a DECISION rather than a default.
   *
   * So: do not delete it as dead weight. Deleting it silently removes a
   * public pricing row while the Figma frames still carry it in all four
   * responsive variants, which is a pricing call and not a cleanup.
   */
  interactions?: boolean
  /** Event → action automation builder (AGL-148). */
  actions?: boolean
  /** Outbound/inbound webhooks (AGL-149). */
  webhooks?: boolean
  /** Customer REST API v1 + API keys (AGL-615); Business tier. */
  apiAccess?: boolean
  /** Whole-site export/backup + restore (AGL-163). */
  siteExport?: boolean
  /** Multilingual sites (AGL-164): locale variants + switcher. */
  multilingual?: boolean
  /** Event Calendar add-on (AGL-145); paid, not part of any base tier. */
  eventCalendar?: boolean
  /** URL redirects manager (AGL-154). */
  redirects?: boolean
  /** Per-screen traffic analytics (AGL-150). */
  screenAnalytics?: boolean
  /** CDN delivery + responsive image variants for media (AGL-175). */
  mediaCdn?: boolean
  /** Announcement bar + promotional popups (AGL-195/196). */
  marketingOverlays?: boolean
  /** Full storefront commerce: catalog, cart, checkout (AGL-278). */
  commerce?: boolean
  /** Console point-of-sale mode (AGL-312). */
  pos?: boolean
  /** Entitlement-gated screens/sections/video paywalls (AGL-309). */
  contentGating?: boolean
  /** Verified-buyer product reviews (AGL-324). */
  productReviews?: boolean
  /** Abandoned checkout recovery emails (AGL-323). */
  abandonedCart?: boolean
  /** Dropship supplier routing on paid orders (AGL-289). */
  dropshipRouting?: boolean
  /**
   * White-label the platform (White-Label Phase 1): replace the Aglyn brand
   * — product name, logo, colors, support URL, transactional email from-name
   * — with the org's own `brandingProfile` across every branded surface.
   * Agency ($799) and Enterprise carry it on the plan (AGL-1118); any other
   * tier needs a per-org `entitlements` override. Strictly broader than
   * `removeBranding`, which only drops the
   * "Made with Aglyn" badge on published sites; white-label REPLACES the
   * brand rather than merely hiding it. Every branded surface resolves the
   * effective brand through `resolveBrandingProfile` so it can never drift.
   */
  whiteLabel?: boolean
  /**
   * Enterprise SSO (AGL-1101): the org's console users sign in through the
   * org's own SAML/OIDC IdP, wired as a per-org GCIP tenant (`org.sso`).
   * Distinct from `whiteLabel` — an Agency org can have one without the other.
   * Carried by the `enterprise` plan (AGL-1118) and false on every other base
   * plan; a lower tier needs a per-org `entitlements` override, which is how
   * enterprise orgs provisioned before that plan existed still get it. Gates
   * the staff SSO-config card and the SSO sign-in path; a non-entitled org can
   * neither configure nor use SSO.
   */
  ssoEnabled?: boolean
}

/**
 * Boolean feature gates per plan, the platform's and its plugins' together.
 *
 * {@link CoreOrgFeatureFlags} is the platform's own set — what
 * `PLAN_ENTITLEMENTS` declares for every plan, and what
 * `Required<CoreOrgFeatureFlags>` keeps exhaustive.
 * {@link PluginEntitlementFeatures} is the plugin half (AGL-3124): a plugin
 * declares a gate of its own into it with `declare module`, and its answer
 * per plan comes from that plugin's `plan-entitlements` declaration or its
 * `registerPluginEntitlements` defaults rather than from a row core writes —
 * which is why a plugin's gate joins HERE and not above. Every reader keeps reading `OrgFeatureFlags`, and
 * `keyof OrgFeatureFlags` admits a plugin's key the moment it is declared.
 */
export interface OrgFeatureFlags
  extends CoreOrgFeatureFlags,
    PluginEntitlementFeatures {}

/**
 * An org's white-label brand identity (White-Label Phase 1). Populated on
 * the org doc (`orgs/{orgId}.brandingProfile`) and applied ONLY when the org
 * carries the `whiteLabel` entitlement (Agency or Enterprise plan, or a
 * per-org override);
 * otherwise every surface falls back to the Aglyn defaults baked into
 * `resolveBrandingProfile`. Every field is optional — a partial profile
 * still resolves, with the Aglyn default filling each gap — so an agency can
 * set just a product name and from-name without supplying logos.
 */
export interface OrgBrandingProfile {
  /** Brand name shown in place of "Aglyn" (console chrome, emails, badges). */
  productName?: string
  /** Primary/full-color logo URL (light backgrounds, console chrome). */
  logoUrl?: string
  /**
   * The logo for DARK grounds (AGL-3406): the console chrome in dark mode and the
   * published site's attribution badge, which always sits on a dark pill.
   * Unset falls back to {@link logoUrl}.
   */
  logoDarkUrl?: string
  /** Favicon URL for branded surfaces. */
  faviconUrl?: string
  /**
   * The favicon for a browser whose tab strip is dark, emitted as a second
   * `<link rel="icon">` under `(prefers-color-scheme: dark)`. Unset falls back
   * to {@link faviconUrl}.
   */
  faviconDarkUrl?: string
  /** Brand primary color as a CSS color (hex), e.g. `#1a73e8`. */
  primaryColor?: string
  /** Support/help destination linked from branded surfaces and emails. */
  supportUrl?: string
  /** Transactional email from-name (the display name before the address). */
  fromName?: string
  /** Logo URL specifically for the email header (often a hosted PNG). */
  emailLogoUrl?: string
  /** Custom console domain the agency serves the app on (Phase 4 wiring). */
  customConsoleDomain?: string
}

/**
 * An org's enterprise SSO configuration (AGL-1101, Phase 1). Applied ONLY when
 * the org carries the `ssoEnabled` entitlement. The actual IdP lives in a
 * per-org **GCIP tenant** (`tenantId`) with a SAML/OIDC **provider**
 * (`providerId`); this block is the org-doc mirror the console reads to route
 * sign-in and the staff card edits. A public `ssoDomains/{domain}` doc maps a
 * verified email domain → `{ orgId, tenantId, providerId }` so the
 * pre-auth sign-in page can resolve an SSO org without reading the org doc.
 *
 * Security: a domain reaches `domains[]` ONLY by passing DNS TXT verification
 * (AGL-1210). This is the account-takeover guard, and it is the whole reason
 * self-serve is safe: without it an org could claim a domain it does not own,
 * `ssoDomains/{domain}` would route that domain's sign-ins to its IdP, and it
 * would intercept another company's logins. Claims in flight live in
 * `orgs/{orgId}/ssoDomains/{domain}` (see `OrgSsoDomainClaim`) and are NOT
 * governed until verified.
 *
 * `enforced` blocks password/social login for the governed domains, so it is a
 * lockout risk and is rehearsed (`previewSsoEnforcement`) before it is applied.
 */
export interface OrgSsoConfig {
  /** GCIP tenant id that carries this org's IdP provider. */
  tenantId: string
  /** GCIP provider id, e.g. `saml.aglyn-workspace` or `oidc.acme`. */
  providerId: string
  /** IdP protocol. Phase 1 ships SAML; OIDC is Phase 2. */
  protocol: 'saml' | 'oidc'
  /** Human label for the IdP (shown on the SSO button + staff card). */
  displayName?: string
  /**
   * Email domains routed to this IdP (lowercased, no `@`). A domain is added
   * here only after its claim passes DNS TXT verification, and removed the
   * moment re-verification fails — so membership of this array IS the
   * "ownership proven" statement. Never write to it from anywhere but the
   * verification path.
   */
  domains: string[]
  /**
   * Retained for `sso-jit`, which gates on it. Kept in lockstep with
   * `domains.length > 0` by the verification path; it was a staff-attested
   * boolean before AGL-1210 and is now derived, never asserted by a human.
   */
  domainVerified: boolean
  /**
   * SAML metadata the CUSTOMER supplies about their IdP. Stored so the pool's
   * provider config can be rebuilt or re-applied without asking again. The
   * X.509 certificate is a public signing certificate, not a secret.
   */
  idp?: {
    entityId: string
    ssoUrl: string
    certificates: string[]
  }
  /**
   * Require SSO for the governed domains — disables password/social login for
   * them (Phase 2). Phase 1 keeps this false so users keep a fallback.
   */
  enforced: boolean
  /** Lifecycle: `configuring` (not live), `active`, or `disabled`. */
  status: 'configuring' | 'active' | 'disabled'
  /**
   * Uid + time of the last config change. Since AGL-1210 this is normally an
   * ORG ADMIN, not staff — the flow is self-serve end to end.
   */
  configuredBy?: string
  configuredAt?: ITimestamp
}

/**
 * A pending or proven claim on one email domain (AGL-1210), stored at
 * `orgs/{orgId}/ssoDomains/{domain}`.
 *
 * A subcollection rather than a map on the org doc because domains contain
 * dots, and a dotted key in a Firestore map is read as a nested field path by
 * every update helper — writing `sso.domainClaims["acme.com"]` would silently
 * create `{acme: {com: …}}`. A document id has no such ambiguity.
 *
 * The claim is deliberately worthless on its own: holding one grants nothing.
 * Only `verified` moving to true adds the domain to `sso.domains`, and only
 * that array is consulted at sign-in.
 */
export interface OrgSsoDomainClaim {
  /** The domain being claimed (lowercased, no `@`); mirrors the document id. */
  domain: string
  /**
   * Random value the org must publish as a DNS TXT record at
   * `_aglyn-challenge.<domain>`. Per org+domain, so two orgs claiming the same
   * domain get different tokens and neither can pass on the other's record.
   */
  token: string
  /** True once a DNS lookup has actually seen `token` at the challenge host. */
  verified: boolean
  createdAt: ITimestamp
  verifiedAt?: ITimestamp
  /** Last lookup attempt, successful or not — drives re-verification. */
  lastCheckedAt?: ITimestamp
  /**
   * TXT records seen on the last FAILED lookup. Shown back to the customer,
   * because "no record found" and "found the wrong value" are different
   * mistakes and the fix differs.
   */
  lastRecords?: string[]
}

/**
 * Effective limits/gates for a tenant. Plan defaults come from
 * `PLAN_ENTITLEMENTS` (versioned with the app); per-tenant overrides can be
 * stored on the tenant doc and win over the plan defaults.
 *
 * The CORE half. Read {@link OrgEntitlements} instead: it is this plus
 * whatever the plugins declare. The keys spelled out below predate the seam
 * and move to their plugins with the rest of AGL-3080; every NEW key is
 * declared by whoever owns it.
 */
export interface CoreOrgEntitlements {
  hostLimit?: number
  screensPerHost?: number
  sharedLayoutsPerHost?: number
  /** Saved templates per host (AGL-666) — includes marketplace downloads. */
  templatesPerHost?: number
  // NOTE (AGL-658): "add-on" means two unrelated things in this codebase.
  // `seatAddons` below are BILLING capacity — extra managers, hosts, seats —
  // surfaced in the UI as "plan add-ons". The marketplace sense (installed
  // plugins, the `orgAddons` slot) is a different concept entirely. The
  // marketplace owns the bare word; billing copy always qualifies it.
  // Firestore and Stripe lookup keys stay as they are — they are persisted.
  storagePerHostMb?: number
  /**
   * RETIRED (AGL-2133). Enforced by nothing and unreachable by measurement —
   * see `RETIRED_ENTITLEMENT_KEYS`. It stays on the TYPE because live org
   * documents still carry staff overrides of it and `OrgEntitlements` is the
   * shape those documents are read through; `resolveOrgEntitlements` drops
   * it, and no plan declares it any more, so nothing can resolve a value.
   * @deprecated
   */
  totalSiteSizeMb?: number
  /**
   * Included per-site COLLABORATOR seats (`hosts/{id}/members`,
   * viewer/editor/admin) — console teammates scoped to one site, not
   * end-user member accounts (`siteMembers`), which are unlimited on
   * every plan (AGL-888/889). Legacy key name; persisted, do not rename.
   */
  membersPerHost?: number
  /** Seat model (AGL-112): included tenant-manager seats. */
  managersPerOrg?: number
  /** Hard seat caps incl. purchased addons; beyond these, upgrade the plan. */
  maxManagersPerOrg?: number
  /** Hard per-site collaborator cap incl. addons (see `membersPerHost`). */
  maxMembersPerHost?: number
  bandwidthGb?: number
  /**
   * Saved form DEFINITIONS per host — documents under
   * `hosts/{hostId}/forms`, the entity a submission's `formId` points at.
   *
   * Not the same dimension as `formSubmissionsPerMonth`, and the two are
   * routinely confused: this is how many distinct intake forms an author may
   * build, that is how many replies the site may accept. Only the second is
   * tiered. This one resolves to the same `FORMS_PER_HOST_CEILING` on every
   * plan that has the form entity at all — it bounds a collection rather than
   * selling a tier — and exists as an entitlement so that `checkQuota` can
   * refuse a create, and so a contract can override one org's number.
   *
   * Counts the CATALOG, never the traffic. A `Form` node drawn on a page and
   * left unbound submits without a definition and spends nothing here, which
   * is why a plan resolving to 0 still accepts submissions.
   */
  formsPerHost?: number
  /**
   * Reusable component DEFINITIONS per host — documents under
   * `hosts/{hostId}/components` (AGL-3615). Free 1; every paid plan
   * `UNLIMITED`, the allowance it had when components were a boolean
   * feature. Refused at the create only, so a site holding more than its
   * plan now includes keeps every component.
   */
  componentsPerHost?: number
  /** Component-builder caps (AGL-99): host variables. */
  variablesPerHost?: number
  /** Component-builder caps (AGL-99): host functions. */
  functionsPerHost?: number
  /** Workflow builder cap (AGL-99/101). */
  workflowsPerHost?: number
  /** Bookable services per host (AGL-159). */
  servicesPerHost?: number
  /** Redirect rules per host (AGL-154). */
  redirectsPerHost?: number
  /**
   * The CRM RECORDS band (AGL-197, widened in AGL-2611): contacts, companies
   * and deals, counted together across the org. The persisted key still says
   * "contacts" because it is written on live org documents as a staff
   * override and read back through this type; every customer surface says
   * "CRM records". A tier with an `extraContactsUsdPer1k` rate meters past
   * it; a tier with none refuses the next record. Free has no CRM
   * (AGL-2851), so on Free the record its band refuses is a capture. Tasks
   * and activities are not counted — see `CRM_ACTIVITIES_PER_RECORD_CEILING`.
   */
  contactsPerHost?: number
  /**
   * CAMPAIGN emails sendable per calendar month (AGL-161), and campaign
   * emails only (AGL-1438).
   *
   * Transactional mail — password resets, invites, order confirmations,
   * booking reminders, workflow notifications — is never refused by this cap
   * at any tier. It still COUNTS toward the org's email cost meter; it simply
   * cannot be blocked, because a quota that can drop a password reset locks
   * somebody out of their own account and a dropped order confirmation reads
   * to the buyer as a failed order.
   *
   * The name is the narrow thing on purpose. A cap that means something
   * narrower than it says is how AGL-1438 came to exist, so every surface
   * that shows this number says "campaign".
   *
   * Sized against shared-domain reputation, which is a different principle
   * from the one behind `crmEmailsPerDay` — see there for why the two ladders
   * are not meant to track each other.
   */
  emailSendsPerMonth?: number
  /** Action runs per calendar month (AGL-148). */
  actionRunsPerMonth?: number
  /** Included customer REST API requests per calendar month (AGL-634);
   * Beyond it, metered overage per 1,000 where the plan prices it. Only
   * Business/Advanced carry `apiAccess`, so lower tiers are 0. */
  apiRequestsPerMonth?: number
  /** Dynamic data caps — org-scoped (AGL-239/240): datasets are shared
   * by every host in the org, so counts and size meter per org. */
  datasetsPerOrg?: number
  /** Hard dataset cap incl. addons (AGL-132/240); beyond it, upgrade. */
  maxDatasetsPerOrg?: number
  recordsPerDataset?: number
  /** Included aggregate dataset storage (MB) across the org (AGL-240);
   * Beyond it, metered overage per GB where the plan prices it. */
  dataStorageMbPerOrg?: number
  /** @deprecated Legacy host-keyed override (pre-AGL-240); resolved into
   * `datasetsPerOrg` by `resolveOrgEntitlements`. */
  datasetsPerHost?: number
  /** @deprecated Legacy host-keyed override (pre-AGL-240); resolved into
   * `maxDatasetsPerOrg` by `resolveOrgEntitlements`. */
  maxDatasetsPerHost?: number
  /** Catalog products per host (AGL-278). */
  productsPerHost?: number
  /** Inventory locations per host (AGL-286). */
  inventoryLocations?: number
  /** Concurrent POS registers (AGL-312); add-ons raise it (AGL-329). */
  posRegisters?: number
  /** Platform fee % on physical storefront sales (Connect app fee). */
  transactionFeePhysicalPct?: number
  /** Platform fee % on digital storefront sales (Connect app fee). */
  transactionFeeDigitalPct?: number
  features?: OrgFeatureFlags
  /**
   * A staff PLAN COMP (AGL-3034) — see `OrgPlanComp`. Not a quota: the
   * resolver's numeric loop skips it, and `ResolvedOrgEntitlements` omits
   * it. Read through `readOrgPlanComp`, never directly.
   */
  planComp?: OrgPlanComp
}

/**
 * Effective limits and gates for a tenant, the platform's and its plugins'
 * together (AGL-3124).
 *
 * {@link CoreOrgEntitlements} is what `PLAN_ENTITLEMENTS` declares per plan
 * and what `ResolvedOrgEntitlements` keeps exhaustive.
 * {@link PluginEntitlementQuotas} is the plugin half: a plugin declares a
 * key of its own into it with `declare module`, and its figure per plan comes
 * from that plugin's `plan-entitlements` declaration, compiled into
 * `PLAN_ENTITLEMENTS` (AGL-3080), widened by any seat add-on it declares. So
 * `resolveOrgEntitlements` carries a plugin's band, and `keyof
 * OrgEntitlements` admits its key, without this file naming the plugin's
 * domain.
 */
export interface OrgEntitlements
  extends CoreOrgEntitlements,
    PluginEntitlementQuotas {}

/**
 * A plan staff GRANTED a workspace that no live subscription pays for
 * (AGL-3034), stored at `orgs/{orgId}.entitlements.planComp`.
 *
 * ## Why a marker of its own, and not the stored `plan`
 *
 * `plan: 'pro'` beside `billingStatus: 'canceled'` is what EVERY customer who
 * canceled can look like: Stripe's mirror is not always walked back to
 * `free`, and a staff override used to write `plan` directly. So a stored
 * paid plan must never outrank a dead subscription — `resolveEffectivePlan`
 * reads it as Free, which is AGL-247's rule and stays it. A comp is a
 * different fact with a different writer: only `/api/admin/org-override`
 * writes this, audited, with a reason. Nothing reads a comp out of `plan`.
 *
 * ## When it applies
 *
 * Only while the subscription is dead or absent — `resolvePlanComp`. A live
 * subscription (and any status the resolver does not read as dead) governs
 * the plan, and the comp waits, dormant, until staff remove it or the
 * subscription ends.
 *
 * ## Why under `entitlements`
 *
 * It sits beside the other staff overrides, on the one map the override
 * route already owns and the rules already deny to every client, staff
 * included (AGL-1795). A top-level key would have needed a new entry in all
 * three of the org rule's deny-lists, in a rules file at its size limit.
 *
 * ## What it is NOT
 *
 * Not a sale. A comp bills nothing, so every metered band on it is a wall —
 * `resolvePlanPricing` — and it is never a card on file or a paying
 * subscription: `isBillingSubscription` stays false, and the AI overage
 * charge path has no rate to bill it at.
 *
 * ## Capped or uncapped (AGL-3049)
 *
 * A comp is CAPPED unless staff lift it: every band is its plan's figure, a
 * hard limit, and a per-org quota override beside the comp raises one band.
 * An UNCAPPED comp (`uncapped: true`) reads every band and quota as
 * unlimited while it is in force — the shape an internal workspace needs —
 * and still bills nothing. The flag is part of the grant: the same route
 * writes it with its own reason and audit row, removing the comp removes it,
 * and a live subscription ignores it along with the rest of the comp.
 *
 * The route states it on every grant, `false` included. The org write is a
 * merge, which writes a nested map key by key, so a capped grant that left
 * the key out would keep the `true` of the uncapped comp it replaced.
 *
 * Typed loosely where foundation cannot import the vocabulary: `reason` is
 * an `OrgOverrideReasonCode` (`app-utils/org-override-reason`), narrowed by
 * `readOrgPlanComp`.
 */
export interface OrgPlanComp {
  /** The plan granted. A paid plan; a comp of `free` is not a comp. */
  plan: OrgPlan
  /**
   * Every band and quota reads as unlimited while the comp is in force
   * (AGL-3049). Only a literal `true` lifts them; absent means capped.
   */
  uncapped?: boolean
  /** Why, from the override's fixed reason set (AGL-1652). */
  reason: string
  /** Staff-only rationale; explicit `null` when none was given. */
  note: string | null
  /** The staff uid that granted it. */
  grantedBy: string
  /** When it was granted — the server's clock. */
  grantedAt: ITimestamp | null
}

/**
 * Paid addon quantities (AGL-112/524) purchased on top of the plan's
 * included allowances, billed as items on the org's Stripe subscription.
 * Seat/dataset kinds resolve as `included + purchased` clamped to the
 * plan's hard max — beyond the max the org must upgrade. Purchases only
 * count while the subscription is alive (they bill on it); staff grants
 * live on `entitlements` overrides instead, so the two never collide.
 */
export interface OrgSeatAddons {
  /** Extra tenant-manager seats. */
  managers?: number
  /**
   * Extra per-site collaborator seats. Legacy key name (AGL-888): the Stripe
   * price env suffix is still `EXTRA_MEMBER` and existing org docs carry this
   * key — persisted, do not rename.
   *
   * A POOL, not a raise (AGL-2439) — the same correction `posRegisters`
   * received in AGL-1775, applied to the key that never got it.
   * `membersPerHost` is enforced PER SITE, so folding this org-wide quantity
   * into the org-level entitlement handed every site the whole purchase: one
   * extra collaborator seat bought 20 collaborators on a 20-site org. Since
   * AGL-2439 the quantity here is the size of an org-level pool and
   * `collaboratorAllocations` says which site each purchased seat is assigned
   * to. Nothing reads this as a per-site number; use
   * `checkHostCollaboratorQuota` / `resolveHostCollaboratorCap`.
   */
  members?: number
  /** Extra org datasets (AGL-132/240); billed monthly per dataset. */
  datasets?: number
  /** Extra sites beyond the plan's `hostLimit` (AGL-68/524). */
  hosts?: number
  /**
   * Extra POS registers beyond the plan's `posRegisters` (AGL-329/524).
   *
   * A POOL, not a raise (AGL-1775). `posRegisters` is enforced PER SITE, so
   * folding this quantity into the org-level entitlement handed every site
   * the whole purchase — one $89/mo register bought 20 registers on a
   * 20-site org. Since AGL-1775 the quantity here is the size of an org-level
   * pool and `registerAllocations` says which site each purchased seat is
   * assigned to. Nothing reads this as a per-site number; use
   * `resolveHostRegisterCap`.
   */
  posRegisters?: number
  /** Event Calendar org-wide toggle, 0/1 (AGL-145/524). */
  eventCalendar?: number
  /**
   * Aglyn AI org-wide toggle, 0/1 (AGL-2896). One purchase covers every
   * host in the org, the way Event Calendar does: `resolveOrgEntitlements`
   * reads any quantity >= 1 as one, adds the plan's
   * `AI_ADDON_CREDITS_PER_MONTH` band to `assistCreditsPerMonth`, and
   * switches `features.aiGenerative` and `features.aiAssist` on. Priced per
   * plan at `PlanPricing.aiAddonMonthlyUsd`; `null` there means the plan
   * does not sell it.
   */
  aiAddon?: number
}

/**
 * Which SITE each purchased POS register seat is assigned to (AGL-1775):
 * `{ [hostId]: seats }`, drawn from the org-level pool
 * `seatAddons.posRegisters`.
 *
 * WHY IT LIVES ON THE ORG DOC. The pool is org-level and the entitlement it
 * modifies is resolved from this same document, so a caller that can resolve
 * entitlements at all already holds the allocation — there is no second read
 * to fail and no separate loading state in which a host could resolve to
 * something other than its plan cap. That property is the point: the
 * `checkQuota(undefined)` = Free-tier lesson inverted, where an absent
 * allocation must mean the PLAN's cap and never the pooled total.
 *
 * AN ENTITLEMENT INPUT, and therefore Admin-SDK-only — it is denied to every
 * client in `cloud/firebase-firestore.rules` alongside `seatAddons` and
 * `entitlements`. A client that could write this could assign itself the
 * whole pool on every site, which is the defect AGL-1775 exists to close.
 *
 * A host id absent from the map holds ZERO purchased seats and resolves to
 * the plan cap alone. Deleting a site deletes its key, which returns its
 * seats to the pool — the pool is `seatAddons.posRegisters` minus the sum of
 * this map, so a released key is available capacity by arithmetic rather
 * than by a separate counter that could drift.
 */
export type OrgRegisterAllocations = Record<string, number>

/**
 * Which SITE each purchased COLLABORATOR seat is assigned to (AGL-2439):
 * `{ [hostId]: seats }`, drawn from the org-level pool `seatAddons.members`.
 *
 * The same object as `OrgRegisterAllocations` and for the same reasons —
 * see that type's block, all of which applies verbatim. It is a distinct type
 * because it is a distinct field on the document and the two pools are bought
 * separately; nothing may read one as the other.
 *
 * AN ENTITLEMENT INPUT, and therefore Admin-SDK-only — denied to every client
 * in `cloud/firebase-firestore.rules` alongside `seatAddons`,
 * `registerAllocations` and `entitlements`. A client that could write this
 * could assign itself the whole pool on every site, which is the defect the
 * pool exists to close.
 *
 * A host id absent from the map holds ZERO purchased seats and resolves to
 * the plan cap alone. Deleting a site deletes its key, returning its seats to
 * the pool by arithmetic rather than by a counter that could drift.
 *
 * ONE THING DIFFERS FROM REGISTERS and it is deliberate: collaborator seats
 * clamp to the plan's `maxMembersPerHost` band, because that is how they are
 * sold. Assigning ten pool seats to a Starter site cannot lift it past ten —
 * `resolveHostCollaboratorCap` applies the clamp, so the console must not
 * present an assignment past the band as capacity.
 */
export type OrgCollaboratorAllocations = Record<string, number>

/**
 * The monthly storage-overage spend cap an org CHOSE for itself (AGL-1886,
 * corrected 2026-08-18).
 *
 * The ceiling on metered spend is the END USER's control: it exists to prevent
 * a surprise, not to gate the product on a customer's acknowledgement.
 *
 * So this document is **absent by default and absent is the normal state**.
 * Storage past a metered plan's included band bills without it; the customer
 * is warned by `usage-alerts` on approach and at the band. Writing a cap here
 * is a customer opting IN to being stopped, and it is the only thing that can
 * refuse an upload on a plan that meters.
 *
 * NOT a consent record. As an acknowledgement that must exist before a paying
 * org may store a byte past its band, it costs revenue and blocks customers
 * without preventing any bill — `report-usage` bills stored bytes and never
 * reads this document.
 *
 * Still an ENTITLEMENT INPUT in the security sense, in the other direction: a
 * member who could raise their own `capUsd` could raise their own spend
 * ceiling, so the rules deny it to every client and only
 * `/api/billing/storage-overage` (Admin SDK, `billing.manage`) writes it.
 *
 * @see apps/console/utils/storage-overage.ts for the model and what still
 * hard-bands.
 */
export interface OrgStorageOverage {
  /**
   * The monthly storage-overage spend the org will not go past. Present means
   * capped; absent means uncapped, which is the default. A present but
   * malformed value resolves to `STORAGE_CAP_FALLBACK_USD` rather than to "no
   * cap" — a customer who asked for a ceiling must not be billed past one
   * because the stored number was corrupt.
   */
  capUsd?: number
  /** When the cap was set, for the audit trail. */
  capSetAt?: ITimestamp | null
  /** The uid that set it. */
  capSetBy?: string | null
  /**
   * LEGACY (pre-2026-08-18): the acknowledged-consent pair. Read only, and
   * honoured as a cap of `monthlyCeilingUsd` so a ceiling somebody typed is
   * not silently raised. Never written again.
   */
  acknowledgedAt?: ITimestamp | null
  /** @deprecated legacy consent field; see `acknowledgedAt`. */
  acknowledgedBy?: string | null
  /** @deprecated legacy bound; read as a cap. See `capUsd`. */
  monthlyCeilingUsd?: number
}

/**
 * The org's own answer to what happens when Aglyn Assist reaches its included
 * band (AGL-2653).
 *
 * Absent by default, and absent is the normal state: assist past the band is
 * SOLD — `reserveAssistMessage` keeps reserving and `report-usage` bills the
 * credits past the band at `PLAN_PRICING.extraAssistCreditsUsdPer1k`. Writing
 * `hardCap: true` is a customer opting IN to being stopped at the band
 * instead, which is what the gate did for every org before the overage was
 * sold. The shape mirrors `OrgStorageOverage`, the same control on storage,
 * for the same reason: a ceiling on metered spend is the END USER's control,
 * offered rather than imposed.
 *
 * An ENTITLEMENT INPUT in the security sense, in both directions at once. A
 * client that could clear it would lift a ceiling the org chose; one that
 * could set it on another org would switch that org's assistant off at the
 * band. So the rules deny it to every client and only
 * `/api/billing/assist-overage` (Admin SDK, `billing.manage`) writes it. Read
 * through `resolveAssistHardCap` in the AI plugin's `usage/assist-credits`,
 * never directly.
 *
 * `report-usage` never reads this map — it bills whatever landed past the
 * band, the way storage bills stored bytes. With the cap on that is at most
 * the one exchange that crossed the line, because an exchange's cost is known
 * only after it is answered; with it off, it is the overage the org chose to
 * buy. Reading the switch at sweep time instead would let a flip on the 1st
 * erase a month's overage, the AGL-2399 shape on a different meter.
 *
 * On a plan whose `extraAssistCreditsUsdPer1k` is `null` the switch changes
 * nothing: there is no rate to sell past the band at, so the band stays the
 * wall it always was, on or off.
 *
 * ## The dollar ceiling beside the switch (AGL-2898)
 *
 * `capUsd` is the second control, and it answers a different question. The
 * switch asks "sell past the band at all?"; the ceiling asks "and if so, how
 * much?" — a monthly figure in USD of OVERAGE, priced at the plan's rate, past
 * which the assistant refuses for the rest of the month. It is the
 * `storageOverage.capUsd` of this meter: optional, absent by default, and the
 * customer's own number rather than a band the plan sold them. Read through
 * `resolveAssistOverageCapUsd`, and enforced by `assistOverageCapReached`
 * inside the same reservation transaction as the band.
 *
 * The two are independent. With the switch on the ceiling is never reached,
 * because nothing past the band is ever sold; with the switch off and no
 * ceiling the overage is open-ended, bounded only by the plan's message cap.
 * On a plan with no rate the ceiling, like the switch, changes nothing:
 * overage on such a plan prices to zero and zero never reaches a ceiling.
 */
export interface OrgAssistOverage {
  /** True stops assist at the included band; absent or false sells past it. */
  hardCap?: boolean
  /** When the switch was last written, for the audit trail. */
  hardCapSetAt?: ITimestamp | null
  /** The uid that wrote it. */
  hardCapSetBy?: string | null
  /**
   * The month's ceiling on AI overage, in USD at the plan's rate. `null` or
   * absent is no ceiling; anything that is not a finite positive number reads
   * as none too, so a corrupt value cannot become a wall of `NaN`.
   */
  capUsd?: number | null
  /** When the ceiling was last written or cleared, for the audit trail. */
  capSetAt?: ITimestamp | null
  /** The uid that wrote it. */
  capSetBy?: string | null
}

/**
 * A discount applied to the org's OWN Aglyn subscription (AGL-1105) — a
 * comped enterprise deal or a redeemed coupon, mirrored from a Stripe coupon
 * so `orgMonthlyRevenueUsd` can report net-of-discount MRR without a Stripe
 * round-trip. Exactly one of `percentOff` / `amountOffUsd` is set, matching
 * the Stripe coupon it points at (Stripe coupons are one or the other).
 * DISTINCT from a storefront/customer discount — this is Aglyn's own bill.
 */
export interface OrgDiscount {
  /** The Stripe coupon id (`co_…`) applied to the subscription. */
  couponId: string
  /** The Stripe promotion code id (`promo_…`), when the coupon has a code. */
  promotionCodeId?: string
  /** The human redemption code (e.g. `LAUNCH25`), when one exists. */
  code?: string
  /** Percentage off, 0–100 (mutually exclusive with `amountOffUsd`). */
  percentOff?: number
  /** Fixed USD off per invoice (mutually exclusive with `percentOff`). */
  amountOffUsd?: number
  /** The staff uid that applied it (audit trail; self-serve reads `system`). */
  appliedBy: UserUid
  /** Free-text why (the enterprise deal, the promotion) — staff-only. */
  reason?: string
  appliedAt: ITimestamp
}

export interface OrgSubscription {
  status?:
    | 'active'
    | 'trialing'
    | 'past_due'
    | 'canceled'
    | 'incomplete'
    | 'unpaid'
  /**
   * WHY the subscription ended, mirrored from Stripe's
   * `cancellation_details.reason` on `customer.subscription.deleted`
   * (AGL-1877). `'payment_failed'` is Stripe having exhausted the dunning
   * retries; `'cancellation_requested'` is the customer leaving on purpose.
   *
   * The distinction is not cosmetic — it is the only thing that tells a
   * DELINQUENT workspace apart from one that simply left, and both arrive as
   * the same event with the same `status: 'canceled'` and the same
   * `plan: 'free'` mirror. Without it `shouldAutoLockOrgForBilling` had no
   * reachable delinquent state at all (see `billing-auto-lock.ts`), the
   * churn funnel counted a dunning failure as voluntary churn, and nobody
   * could be told which had happened.
   *
   * Absent on every org whose subscription ended before this shipped, and
   * every consumer must fail CLOSED on that — an unknown reason is not a
   * proven payment failure.
   */
  canceledReason?: string | null
  priceId?: string
  /**
   * Billing interval of the plan item (AGL-532), webhook-mirrored: the
   * Billing page initializes its monthly/annual toggle from it and plan
   * switches keep it unless the toggle says otherwise.
   */
  interval?: 'month' | 'year'
  currentPeriodEnd?: ITimestamp
  /**
   * A downgrade scheduled for the current period end (AGL-1862). Set by
   * `/api/billing/subscription` when a switch walks DOWN the self-serve
   * ladder — the Stripe subscription schedule owns the transition, this is
   * the manager-gated mirror the billing page renders. `null` (not absent)
   * once released, so a merge clears it. Rides inside `subscription` because
   * `pickOrgBillingFields` drops any other top-level key.
   */
  pendingDowngrade?: {
    plan: string
    interval: 'month' | 'year'
    /** ISO timestamp of the period end the schedule flips at. */
    effectiveAt: string | null
    scheduleId: string
  } | null
  /**
   * Negotiated custom price as a **monthly-normalized** USD figure (AGL-1110),
   * set when an enterprise org bills at an ad-hoc amount rather than a plan's
   * list price — e.g. `agency` capability at $2,730/mo. An annual custom deal
   * is stored ÷12 here (the yearly total lives on the Stripe price), so every
   * reader treats it as monthly and never re-divides. Webhook-mirrored from the
   * Stripe subscription's recurring price. When present it is the truth for
   * revenue — `orgListPriceMonthlyUsd`/`orgMonthlyRevenueUsd` use it instead of
   * the plan default (fixes the custom-price MRR under-report, AGL-1110).
   */
  customMonthlyUsd?: number
}

/**
 * The org's billing/entitlement doc shape — the view of `orgs/{orgId}`
 * that `useCurrentOrg()`, the plugin-page `org` prop, and the
 * entitlement resolvers carry. (Formerly `AglynTenant`; the alias was
 * removed in AGL-444.)
 */
/**
 * `orgs/{orgId}.bandwidthCap` — the engaged free-plan bandwidth cap
 * (AGL-1967/2070/2155).
 *
 * STAMPED WITH THE MONTH IT IS FOR, and never cleared. A new month simply
 * stops matching, so the cap lifts on its own with no write: a marker whose
 * removal depends on a cron running is a marker that stays engaged when the
 * cron fails, which would take a customer's site down over an infrastructure
 * problem rather than over their traffic.
 *
 * Plain numbers rather than `ITimestamp`, matching `suspendedUntilMs`, so the
 * value survives the tenant's cache serialization unchanged.
 */
export interface OrgBandwidthCap {
  /** UTC `YYYY-MM` this cap was engaged for. Only the current month refuses. */
  month: string
  /** When the sweep engaged it. Diagnostic only; nothing gates on it. */
  engagedAt?: number
  /** The org-wide page views measured at engage time. Diagnostic. */
  pageViews?: number
  /** The band those page views were measured against. Diagnostic. */
  includedPageViews?: number
}

/**
 * The organization document.
 *
 * NOT composed from a plugin-augmented interface, deliberately (AGL-3124).
 * `org-write-deny-coverage.spec.ts` enumerates this interface's fields by
 * reading THIS FILE'S SOURCE, and checks every one of them against the
 * Firestore write-deny rules; a field a plugin declared from its own file
 * would be invisible to that sweep, so composing here would open a rules
 * coverage hole to save a plugin an already-solved problem. A plugin's own
 * settings block goes through `registerPluginConfigSchema` into
 * `pluginSettings/{pluginId}`, which is its own document under its own rule.
 */
export interface AglynOrgBilling extends AglynDocument {
  /** The document id, injected by the reader — never a stored field. */
  $id: OrgUid
  /**
   * The org's display name, written by `createOrganization` and renamed
   * through /api/orgs/settings (the name is denormalized onto every
   * membership row, so the rename fans out server-side). Client-writable by
   * an org admin on purpose — see `ORG_CLIENT_WRITABLE_FIELDS`.
   *
   * Spelled `displayName` here until AGL-1355; nothing ever read that, and
   * the document has always carried `name`. The coverage guard derives the
   * org's field set from this interface, so a field that does not exist
   * bought nothing but a blind spot.
   */
  name?: string
  /** Free-text workspace description; nothing gates on it. */
  description?: string
  /**
   * The IANA zone this workspace's published dates read in (AGL-3237) —
   * `America/Chicago`, `Europe/Berlin`. Absent is UTC.
   *
   * What it decides is which CALENDAR DAY an instant is attributed to on a
   * published site, which is an editorial fact about the publisher: a post
   * that went out at 19:30 in Chicago is dated the 21st here and was dated
   * the 22nd while UTC decided it. Sites inherit it; see
   * `resolveSiteTimeZone`.
   *
   * SERVER-OWNED: written through /api/orgs/settings and denied to the
   * client SDK, because `resolveSiteTimeZone` reads it in `app-utils` and
   * the write-deny guard treats a resolver input as server-owned.
   * Nothing bills, gates or routes on it, and it is validated at every read
   * through `isSupportedTimeZone`, so an unusable value renders as UTC rather
   * than throwing inside a page render.
   */
  timeZone?: string
  /**
   * The sites this organization declared ONE SENDER, `{ [groupId]: { name,
   * hostIds } }` (`consent-groups.ts`). Absent, every site is alone.
   *
   * SERVER-OWNED: denied to the client SDK, because `consentGroupForHost`
   * reads it in `app-utils` and decides who a marketing basis covers and whose
   * opt-outs hold a send. The Emails hub's consent group editor changes it
   * through `/api/orgs/consent-groups`, and the one write is the executor's
   * declare step, taken only after every separating site's refusals have
   * been carried both ways (`consent-group-change.ts`).
   */
  consentGroups?: Record<string, { name: string; hostIds: string[] }>
  /**
   * A consent group change in flight (AGL-3320): `{ changeId, phase,
   * hostIds, startedAtMs, declaredAtMs? }`, present exactly while one runs
   * and cleared when it finishes or is canceled. The job it names is
   * `orgs/{orgId}/consentGroupChanges/{changeId}`.
   *
   * SERVER-OWNED: written only by the executor, which relies on it as the
   * lock — a second change, and a deletion of a site it names, are refused
   * while it stands — so a client able to clear it could run two changes at
   * once, and one able to set it could block every change after it.
   */
  consentGroupsChange?: {
    changeId: string
    phase: 'carry' | 'rehome' | 'sweep'
    hostIds: string[]
    startedAtMs: number
    declaredAtMs?: number
  }
  /**
   * Whether a declared group's sites wait for each other's confirmation click
   * (AGL-3316). Absent or `false` is off: a pending confirmation holds only
   * the asking site's mail.
   *
   * SERVER-OWNED: written through /api/orgs/settings and denied to the client
   * SDK, for the reason `consentGroups` is — `consentGroupForHost` resolves it
   * into every group a send path reads.
   */
  consentGroupsAwaitConfirmation?: boolean
  /** The workspace URL segment, reserved through `orgSlugs/{slug}`. */
  slug?: string
  /**
   * The owner's uid. Spelled `ownerId` here until AGL-1355 — every reader
   * has always used `ownerUid` (the key `createOrganization` writes and the
   * rules deny), so the declared name was dead.
   */
  ownerUid?: UserUid
  hosts?: Record<HostUid, true>
  /** Subscription tier; missing/unknown plans resolve as `free`. */
  plan?: OrgPlan
  /** Per-org entitlement overrides (admin console); win over plan defaults. */
  entitlements?: OrgEntitlements
  /**
   * Per-org RELEASE-flag overrides (AGL-1635) — a different axis from
   * `entitlements`, which asks what the org's plan includes. These ask
   * whether an unreleased feature is switched on for this one customer, and
   * win over both the Remote Config value and the rollout bucket.
   *
   * Staff-only, and deliberately narrower than `entitlements`: super staff
   * alone may write it, matching the platform-wide flag editor
   * (`/api/admin/flags` is super-only), because forcing a flag on for an org
   * is the same class of act as flipping it for everyone — just scoped.
   * Read through `parseOrgReleaseFlagOverrides`, never directly.
   *
   * Typed loosely on purpose. The keys ARE `ReleaseFlagKey`, but that union
   * lives in `app-utils/release-flags` and foundation cannot import
   * app-utils (see `platform.types.ts`). Declaring the narrow type here
   * would also overstate what is on disk: this map outlives registry
   * renames, so a retired key is a thing that genuinely exists in Firestore.
   * `parseOrgReleaseFlagOverrides` is what narrows it, dropping unknown keys
   * and non-booleans.
   */
  releaseFlags?: Record<string, boolean>
  /**
   * White-label brand identity (White-Label Phase 1). Applied only when the
   * org carries the `whiteLabel` entitlement; read exclusively through
   * `resolveBrandingProfile`, never directly, so no surface diverges.
   */
  brandingProfile?: OrgBrandingProfile
  /**
   * Enterprise SSO config (AGL-1101). Applied only when the org carries the
   * `ssoEnabled` entitlement; the console routes sign-in through the org's
   * GCIP tenant/provider named here. See `OrgSsoConfig`.
   */
  sso?: OrgSsoConfig
  /** Per-org plugin switchboard (AGL-416); see plugin-manager/enabled-plugins. */
  enabledPlugins?: string[]
  /** Purchased addon seats (AGL-112); billed monthly per seat. */
  seatAddons?: OrgSeatAddons
  /**
   * POS register seats assigned out of the org pool (AGL-1775). An
   * ENTITLEMENT INPUT: it raises a per-site cap, so it is Admin-SDK-only.
   * @see OrgRegisterAllocations
   */
  registerAllocations?: OrgRegisterAllocations
  /**
   * Per-site collaborator seats assigned out of the org pool (AGL-2439). An
   * ENTITLEMENT INPUT: it raises a per-site cap, so it is Admin-SDK-only.
   * @see OrgCollaboratorAllocations
   */
  collaboratorAllocations?: OrgCollaboratorAllocations
  /**
   * The free plan's engaged BANDWIDTH CAP (AGL-1967/2070/2155), denormalized
   * onto this doc by the `usage-alerts` cron so the serving path can refuse a
   * capped site without a read of its own.
   *
   * An ENTITLEMENT INPUT in the strongest sense: it is the only thing standing
   * between a free site that has blown its band and unmetered egress, so a
   * client-writable value would let an org admin lift their own cap by
   * deleting one field. Admin-SDK only; denied in the rules.
   *
   * Read exclusively through `bandwidthCapEngaged` (`app-utils/bandwidth-cap`),
   * never directly — the marker on its own is not the answer. The resolver
   * re-derives the plan on every read, which is what lets an org that upgrades
   * mid-month start serving again without waiting for a cron to clear it.
   */
  bandwidthCap?: OrgBandwidthCap
  /**
   * The org's assist hard-cap switch (AGL-2653) — see `OrgAssistOverage`.
   * Admin-SDK-only, denied in the rules beside `storageOverage`; read
   * through `resolveAssistHardCap`.
   */
  assistOverage?: OrgAssistOverage
  stripeCustomerId?: string
  subscription?: OrgSubscription
  /**
   * Discount on the org's own Aglyn subscription (AGL-1105) — staff-applied
   * enterprise deal or a redeemed coupon. `orgMonthlyRevenueUsd` subtracts it
   * for net-of-discount MRR; `orgListPriceMonthlyUsd` ignores it (list price).
   */
  discount?: OrgDiscount
  /**
   * Explicit **comped enterprise** marker (AGL-1110). Makes the org read as
   * "Enterprise" (via `isEnterpriseOrg`) without a negotiated custom price —
   * for internal/dogfood accounts (e.g. Aglyn's own org) that carry full
   * Enterprise capability + SSO but are 100%-discounted, so they collect $0
   * while infra cost is still metered. Staff-set; distinct from a paying
   * custom-priced enterprise, which qualifies via `subscription.customMonthlyUsd`.
   */
  enterprise?: boolean
  /**
   * The bare `subscription.status` word the Stripe webhook mirrors back onto
   * this doc for the AGL-275 dunning banner and `resolveEffectivePlan`
   * (AGL-1028). An ENTITLEMENT INPUT: a dead subscription downgrades a paid
   * plan to free, so a client-writable value would restore the plan.
   */
  billingStatus?: string
  /** Staff suspension (AGL-202): set = all the org's sites serve 503. */
  suspendedAt?: ITimestamp | null
  suspendedReason?: string
  /**
   * Whether the suspension above is IN FORCE, stored so the staff
   * Organizations list can filter by it with an equality (AGL-3416). `false`
   * on every unsuspended org; written beside the `suspended*` family by the
   * lockdown core and cleared on a lapsed timed lock by
   * `settleLapsedSuspensions`. Never an enforcement input: every enforcing
   * reader asks the family itself.
   */
  suspended?: boolean
  /**
   * GDPR erasure request (AGL-206): hard deletion happens ONLY via `eraseOrg`
   * after a 7-day hold from this stamp — reached by the
   * `/api/admin/run-erasures` cron, or by hand with
   * tools/scripts/erase-tenant.mjs, which calls the same function (AGL-1481).
   */
  erasureRequestedAt?: ITimestamp | null
  /**
   * The CRM's organization-wide settings (AGL-2613), written from
   * CRM → Settings by an owner or admin with the client SDK — see
   * `ORG_CLIENT_WRITABLE_FIELDS`. One map rather than a key per setting, so
   * the section can grow without a rules change each time.
   */
  crm?: OrgCrmSettings
  /**
   * The plan the platform team has asked this workspace to move to
   * (AGL-3466) — see `OrgUpgradeProposal`.
   *
   * SERVER-OWNED: written only by /api/admin/org-upgrade-proposal (staff,
   * audited) and cleared by `writeOrgBilling` once a subscription is live,
   * both Admin SDK. Denied to every client in the rules, staff included,
   * because `standingUpgradeProposal` reads it in `app-utils` and the
   * proposal is the platform's statement to the customer, not theirs.
   */
  upgradeProposal?: OrgUpgradeProposal
  createdAt?: ITimestamp
  updatedAt?: ITimestamp
}

/**
 * The platform team's request that a workspace move to a paid plan
 * (AGL-3466).
 *
 * The separate step that follows an evaluation: a client handed a workspace
 * looks around first, on whatever the workspace already has, and is asked to
 * upgrade only once staff record this. While it stands and no subscription
 * is live, the workspace's billing managers see it on the org home and on
 * Billing, with the plan preselected. Nothing about it grants or bills.
 */
export interface OrgUpgradeProposal {
  /** A paid plan the workspace can buy itself — never `free` or `enterprise`. */
  plan: OrgPlan
  /** The staff member who proposed it. */
  proposedBy: string
  /** Epoch millis, so every cache serialization reads it back unchanged. */
  proposedAt: number
  /** An optional line from staff, shown with the proposal. */
  note?: string
}

/**
 * What the CRM lets an organization decide for every site at once
 * (AGL-2613). Every key is optional and off when absent: a setting that
 * has never been touched behaves as the product did before it existed.
 */
export interface OrgCrmSettings {
  /**
   * Create a company from a captured contact's work email domain when no
   * company the capturing site can see carries that domain. Off by default:
   * a company minted from every domain that ever submitted a form is a list
   * nobody asked for. Public mailbox domains never qualify either way.
   */
  autoCreateCompanies?: boolean
  /**
   * What a new contact, company, deal or task starts shared with
   * (AGL-3662), set apart from datasets and media. `'org'`: every site.
   * `'host'`: the site it came in on and its consent group. Unset reads the
   * org's `defaultResourceScope` — see `crmDefaultScopeOf` — and then
   * `'host'`.
   */
  defaultRecordScope?: 'org' | 'host'
  /**
   * Per-site settings, keyed by host id (AGL-2618). On the ORG document
   * rather than on each host document because the reader is the org-level
   * assignment pass — one read of one document answers every site's default
   * — and because the writer is the same owner-or-admin the rest of this
   * map admits, through the same client branch. A host document would need
   * its own rules clause for a key only the CRM reads.
   */
  hosts?: Record<string, OrgCrmHostSettings>
  /**
   * The assignment rules, in the order they are tried (AGL-2618). The first
   * rule whose every condition holds names the owner; the site's default
   * owner is the fallback when none does. Bounded by
   * `CRM_ASSIGNMENT_RULES_MAX` in the section that writes it.
   */
  assignmentRules?: OrgCrmAssignmentRule[]
  /** The round-robin pool and its pointer — see `OrgCrmRoundRobin`. */
  roundRobin?: OrgCrmRoundRobin
}

/** What one site decides for records captured on it (AGL-2618). */
export interface OrgCrmHostSettings {
  /**
   * The member every record captured on this site is handed to when no
   * assignment rule claimed it. Absent: the record stays unassigned, which
   * is what the product did before the setting existed.
   */
  defaultOwnerUid?: string
}

/**
 * One assignment rule (AGL-2618): `when` every named condition holds for a
 * capture, `assign` the record this way. A condition left out is not a
 * condition — a rule naming only a source matches every capture from that
 * source — and a rule naming nothing matches every capture, which is how a
 * catch-all is written. `source` is a `ContactSource`, typed as a string
 * here because the foundation cannot import the capture vocabulary; the CRM
 * module narrows it.
 */
export interface OrgCrmAssignmentRule {
  id: string
  when: {
    source?: string
    formId?: string
    emailDomain?: string
    tag?: string
  }
  assign: { memberUid: string } | { roundRobin: true }
}

/**
 * The round-robin pool (AGL-2618): the members handed records in turn, and
 * WHO GOT THE LAST ONE. The pointer is a uid rather than an index so that
 * editing the pool — a member added, removed or moved — never skips or
 * repeats anybody: the next record goes to whoever follows the last
 * recipient in the pool as it stands, and to the first member when the last
 * recipient is no longer in it. Advanced only by the server, inside the
 * transaction that writes the owner, so two captures landing together take
 * two different members.
 */
export interface OrgCrmRoundRobin {
  memberUids?: string[]
  lastAssignedUid?: string
}

/**
 * WHO OWNS EACH FIELD OF `orgs/{orgId}` (AGL-1355).
 *
 * AGL-1354 found four server-owned keys — `brandingProfile`, `sso`,
 * `discount`, `enterprise` — that the Firestore rules' org-update key diff
 * had drifted past, so an org admin holding nothing but the Firebase client
 * SDK could write them on any plan. The keys are closed. The MECHANISM that
 * opened them was a hand-maintained deny-list with nothing checking it, and
 * that is what these two maps close.
 *
 * The rule is DEFAULT-DENY, and it is enforced from the interface above:
 * `org-write-deny-coverage.spec.ts` reads every field declared on
 * `AglynOrgBilling`, and one that appears in neither the rules' deny-list nor
 * one of these maps FAILS THE BUILD, naming the field. Adding a field to the
 * interface therefore forces the ownership decision here, at the declaration,
 * on the same commit — instead of shipping client-writable and staying that
 * way until the next audit.
 *
 * So: add a field above, and either add it to the `hasAny([...])` list in
 * `cloud/firebase-firestore.rules` under `match /orgs/{orgId}` (server-owned:
 * anything an entitlement, a price, a routing decision or a staff judgement
 * reads) or add it below WITH A REASON. When in doubt, deny it — a field the
 * server writes through an Admin-SDK route loses nothing by being denied to
 * the client, and that is true of every key AGL-1354 closed.
 *
 * The entries are the fields an org admin may set from the client SDK. The
 * reason is mandatory and is the whole value of the map: it records that
 * someone decided, rather than that someone forgot.
 */
export const ORG_CLIENT_WRITABLE_FIELDS: Readonly<Record<string, string>> = {
  name:
    'The workspace name. Deliberately writable by an org admin — the rules ' +
    'admit the rename branch and `firestore-rules.test.mjs` asserts it still ' +
    'succeeds. Cosmetic: no entitlement, price or routing decision reads it. ' +
    '(The console renames through /api/orgs/settings anyway, because the name ' +
    'is denormalized onto every membership row and the fan-out is a server ' +
    'job — but the client write is allowed and must stay allowed.)',
  description:
    'Free-text workspace blurb. Nothing resolves, gates or bills on it, and ' +
    'no server route owns it.',
  createdAt:
    'Creation stamp seeded by `createOrganization`. Nothing gates on it; the ' +
    'staff org list only displays it. Denying it would buy nothing and would ' +
    'break the merge-writes below, which stamp the pair together.',
  updatedAt:
    'Last-write stamp. Every client write that IS allowed sets it in the same ' +
    '`setDoc(..., { merge: true })` — the staff suspension, erasure-request ' +
    'and plan-override cards all do — so denying it would deny those writes.',
  crm:
    'The CRM settings map (AGL-2613): `autoCreateCompanies`, the per-site ' +
    'default owner, the assignment rules and the round-robin pool ' +
    '(AGL-2618), and whatever the CRM → Settings section adds beside them. ' +
    'Deliberately writable by an org owner or admin — the section writes it ' +
    'client-direct by dotted path. No entitlement, price, routing decision ' +
    'or staff judgement reads it; its readers are the contact capture door ' +
    'deciding whether to mint a company record from an email domain and ' +
    'whom to hand a new record to, which are choices about the org\'s own ' +
    "data that the org's managers are the right people to make. The pool's " +
    'pointer (`crm.roundRobin.lastAssignedUid`) is advanced by the server ' +
    'inside the assigning transaction; a client that moved it would only ' +
    'change who is next, never what anybody may see.',
}

/**
 * Fields declared on `AglynOrgBilling` that are NOT stored on the document,
 * and so cannot be written by anyone. Separate from the map above because
 * "the client may set this" and "this is not a field" are different
 * statements, and collapsing them would let a genuinely client-writable field
 * hide behind a synthetic one.
 */
export const ORG_UNPERSISTED_FIELDS: Readonly<Record<string, string>> = {
  $id: 'The document id, injected by the reader. Never written as a field.',
}

